<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
// 당신의 일간은 / 오행분포!
import { VCol, VRow } from 'vuetify/lib/components/index.mjs';
import fiveMixin from '@/composables/fiveMixin';
import {
  splitFirstSentence,
  splitBySentence,
  transformMarkedText,
} from '@/composables/splitSentence';
import SajuEightTxt from './SajuEightTxt.vue';
import BasicContentsTitle from './BasicContentsTitle.vue';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const { transdata, birthData, birthJijanggan, curDaeun, sajuIlgan, sData } =
  storeToRefs(useAnalysisDataStore());

const langMode = ref('CHNKOR');
const birthTitle = ref('시일월년');
// 일간에 active
const selectedData = ref(2);
const isActive = ref(true);

const { ...five } = fiveMixin();

const 일간특성 = computed(() => sData.value?.['일간특성']);
const 일주특성 = computed(() => sData.value?.['일주특성']);

// 문자열을 파이프(|)로 나눠서 배열로 변환
const splitByPipe = (text) => {
  if (!text) return [];
  return text
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
};

// 일간
const 일간기본특성 = computed(() => splitFirstSentence(일간특성.value?.기본특성));
const 일간핵심문장 = computed(() => 일간기본특성.value.first);
const 일간핵심문장Html = computed(() => transformMarkedText(일간핵심문장.value));
const 일간설명 = computed(() => 일간기본특성.value.restSentences.map(transformMarkedText));
const 일간상세특성 = computed(() =>
  splitBySentence(일간특성.value?.상세특성).map(transformMarkedText),
);
const 일간강점설명 = computed(() =>
  splitBySentence(일간특성.value?.강점설명).map(transformMarkedText),
);

// 일주
const 일주한자 = computed(() => 일주특성.value?.한자 || '');
const 일주한자배열 = computed(() => 일주한자.value.split(''));
const 일간 = computed(() => 일주한자배열?.value?.[0] || '');
const 일지 = computed(() => 일주한자배열?.value?.[1] || '');

// 일주 한자 색상 (idx: 0=일간, 1=일지)
const getIljuHanjaColor = (idx) => {
  if (idx === 0) return five.getColorFive(일간.value);
  if (idx === 1) return five.getColorFive(일지.value);
  return '';
};

// 오행별 배경색 (fortune-cover.scss five01~05 참조)
const FIVE_BG_COLORS = {
  화: 'rgba(194, 53, 53, 0.05)',
  수: 'rgba(64, 64, 64, 0.05)',
  목: 'rgba(94, 159, 28, 0.05)',
  금: 'rgba(148, 148, 148, 0.05)',
  토: 'rgba(219, 152, 35, 0.05)',
};

// 오행별 키워드 배경색 (0.07 투명도)
const FIVE_BG_COLORS_KEYWORD = {
  화: 'rgba(194, 53, 53, 0.07)',
  수: 'rgba(64, 64, 64, 0.07)',
  목: 'rgba(94, 159, 28, 0.07)',
  금: 'rgba(148, 148, 148, 0.07)',
  토: 'rgba(219, 152, 35, 0.07)',
};

// 일간 오행 배경색
const 일간오행배경색 = computed(() => {
  const 오행 = five.getColorFiveText(sajuIlgan.value);
  return FIVE_BG_COLORS[오행] || 'rgba(219, 152, 35, 0.05)';
});

// 일간 오행 키워드 배경색
const 일간오행배경색키워드 = computed(() => {
  const 오행 = five.getColorFiveText(sajuIlgan.value);
  return FIVE_BG_COLORS_KEYWORD[오행] || 'rgba(219, 152, 35, 0.07)';
});

const 일주이름 = computed(() => `${일주특성.value?.일주 || ''}일주`);
const 일주별칭 = computed(() => `“${일주특성.value?.별칭}”` || '');
const 일주한줄특성 = computed(() => 일주특성.value?.한줄특성 || '');
const 일주키워드 = computed(() => 일주특성.value?.키워드 || []);
const 일주성격 = computed(() => splitByPipe(일주특성.value?.성격).map(transformMarkedText));
const 일주대인관계 = computed(() => splitByPipe(일주특성.value?.대인관계).map(transformMarkedText));
const 일주연애스타일 = computed(() =>
  splitByPipe(일주특성.value?.연애스타일).map(transformMarkedText),
);
const 일주인생조언 = computed(() => splitByPipe(일주특성.value?.인생조언).map(transformMarkedText));
</script>

<template>
  <VRow class="ss-full-height">
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card right">
        <BasicContentsTitle
          number="2"
          text="<span>나는 어떤 사람일까?&nbsp;</span><span>(사주로 보는 일간의 특성)</span>"
        />
        <section class="summ-body">
          <div class="summ-body__saju data-sec-01-box">
            <SajuEightTxt
              ref="ManseBirthMainArea"
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-transdata="transdata"
              :pr-type="'birth'"
              :pr-cur-daeun="curDaeun"
              :selected-data="selectedData"
              :is-active="isActive"
              :active-page="'page1'"
            />
          </div>
          <div class="summ-body__result-analysis">
            <div
              class="key-sentence ganji-txt"
              :class="[five.getColorFive(sajuIlgan)]"
              v-html="일간핵심문장Html"
            />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">처음 만났을 때 느껴지는 당신</div>
              <div class="result-analysis__desc" v-for="(line, index) in 일간설명" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">겉으로 보이지 않는 마음의 흐름</div>
              <div class="result-analysis__desc" v-for="(line, index) in 일간상세특성" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">사람들이 놓치기 쉬운 당신의 매력</div>
              <div class="result-analysis__desc" v-for="(line, index) in 일간강점설명" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
          </div>
          <div class="summ-body__result-img"></div>
        </section>
      </div>
    </VCol>
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card">
        <BasicContentsTitle number="3" text="나의 일주 이야기" />
        <section class="summ-body contents05-right">
          <!-- 일주 헤더: 한자 + 일주명 -->
          <div class="ilju-header">
            <div class="ilju-header__hanja">
              <span
                v-for="(char, idx) in 일주한자배열"
                :key="idx"
                class="ilju-header__hanja-char ganji-txt"
                :class="[getIljuHanjaColor(idx)]"
              >
                {{ char }}
              </span>
            </div>
            <div class="ilju-header__name">{{ 일주이름 }}</div>
          </div>

          <!-- 별칭 -->
          <div class="ilju-alias" v-html="transformMarkedText(일주별칭)"></div>

          <!-- 한줄특성 박스 -->
          <div
            class="ilju-oneliner ganji-txt"
            :class="[getIljuHanjaColor(0)]"
            :style="{ backgroundColor: 일간오행배경색 }"
          >
            <span v-html="transformMarkedText(일주한줄특성)"></span>
          </div>

          <!-- 키워드 태그 -->
          <div class="ilju-keywords">
            <span
              v-for="(keyword, idx) in 일주키워드"
              :key="idx"
              class="ilju-keywords__tag ganji-txt"
              :class="[getIljuHanjaColor(0)]"
              :style="{ backgroundColor: 일간오행배경색키워드 }"
            >
              {{ keyword }}
            </span>
          </div>

          <!-- 성격, 대인관계, 연애스타일 그룹 -->
          <div class="ilju-sections-group">
            <!-- 성격 -->
            <div class="ilju-section">
              <div class="ilju-section__title">성격</div>
              <div class="ilju-section__content">
                <p v-for="(line, idx) in 일주성격" :key="'p-' + idx"><span v-html="line"></span></p>
              </div>
            </div>

            <div class="ilju-sections-group__divider" />

            <!-- 대인관계 -->
            <div class="ilju-section">
              <div class="ilju-section__title">대인관계</div>
              <div class="ilju-section__content">
                <p v-for="(line, idx) in 일주대인관계" :key="'r-' + idx">
                  <span v-html="line"></span>
                </p>
              </div>
            </div>

            <div class="ilju-sections-group__divider" />

            <!-- 연애 스타일 -->
            <div class="ilju-section">
              <div class="ilju-section__title">연애 스타일</div>
              <div class="ilju-section__content">
                <p v-for="(line, idx) in 일주연애스타일" :key="'l-' + idx">
                  <span v-html="line"></span>
                </p>
              </div>
            </div>
          </div>

          <!-- 인생조언 박스 -->
          <div class="ilju-advice">
            <div class="ilju-advice__title">인생조언</div>
            <div class="ilju-advice__content">
              <p v-for="(line, idx) in 일주인생조언" :key="'a-' + idx">
                <span v-html="line"></span>
              </p>
            </div>
          </div>
        </section>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
.summ-body.contents05-right {
  gap: 20px;
}

// 일주 이야기 스타일
.ilju-header {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 20px;

  &__hanja {
    display: flex;
    gap: 0;
  }

  &__hanja-char {
    font-family: 'ChosunGs', serif;
    font-size: 40px;
    font-weight: 400;
    line-height: 1.3;
  }

  &__name {
    color: #333;
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }
}

.ilju-alias {
  text-align: center;
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.3;
}

.ilju-oneliner {
  display: flex;
  padding: 8px 20px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
}

.ilju-keywords {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 20px;
  margin-bottom: 24px;
  width: 100%;

  &__tag {
    display: flex;
    padding: 3px 6px;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
  }
}

.ilju-sections-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  &__divider {
    height: 1px;
    background: #ebebeb;
  }

  // 그룹 내 섹션은 margin-bottom 제거
  .ilju-section {
    margin-bottom: 0;
  }
}

.ilju-section {
  margin-bottom: 20px;

  &__title {
    color: #575757;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.5;
    margin-bottom: 8px;
  }

  &__content {
    color: #575757;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.7;

    p {
      margin: 0 0 4px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.ilju-advice {
  padding: 16px;
  border-radius: 8px;
  background: #faf8f5;
  margin-top: 24px;

  &__title {
    color: #f27647;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    margin-bottom: 8px;
  }

  &__content {
    color: #575757;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.7;

    p {
      margin: 0 0 4px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
